import { Skeleton } from '@/components/ui/skeleton'
import { PageHeader } from '@/components/ui/page-header'
import * as React from 'react'

function Card({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return (
    <section
      className={`rounded-lg border border-border bg-card p-4 ${className}`.trim()}
    >
      {children}
    </section>
  )
}

/**
 * Generic content-area skeleton for nested route transitions.
 * Prevents full-page spinners by staying within the main content region.
 */
export function ContentScreenSkeleton(): React.JSX.Element {
  return (
    <div className="animate-pulse flex flex-col gap-4" aria-hidden="true">
      <Skeleton className="h-8 w-1/3 min-w-[200px] bg-muted/30" />
      <Skeleton className="h-40 w-full bg-muted/20" />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Skeleton className="h-32 w-full bg-muted/20" />
        <Skeleton className="h-32 w-full bg-muted/20" />
      </div>
      <Skeleton className="h-24 w-full bg-muted/20" />
    </div>
  )
}

export function MissionsScreenSkeleton(): React.JSX.Element {
  return (
    <div className="flex flex-col gap-4 pb-4">
      <div role="status" aria-live="polite" className="sr-only">
        Carregando missões...
      </div>
      <div className="flex items-start gap-4">
        <PageHeader
          titleAs="h1"
          title="Painel de XP"
          description="Carregando progresso atual..."
          actions={
            <div className="flex items-center gap-2">
              <Skeleton className="h-7 w-28" />
              <Skeleton className="h-7 w-28" />
            </div>
          }
        >
          <Skeleton className="mt-3 h-2.5 w-full" />
        </PageHeader>
        <div className="flex flex-col items-center gap-1">
          <Skeleton className="size-[60px] rounded-full" />
          <Skeleton className="h-3 w-16" />
        </div>
      </div>

      <section className="grid gap-3 lg:grid-cols-3">
        <Card>
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-sm font-bold">Resumo mensal</p>
              <p className="text-xs text-muted-foreground">Mês atual</p>
            </div>
            <Skeleton className="h-4 w-28" />
          </div>
          <p className="mt-3 text-xs text-muted-foreground">Diárias + Semanais</p>
        </Card>
        <Card>
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-sm font-bold">Resumo semanal</p>
              <p className="text-xs text-muted-foreground">Diárias + Semanais</p>
            </div>
            <Skeleton className="h-4 w-28" />
          </div>
          <p className="mt-3 text-xs text-muted-foreground">XP ganho hoje</p>
        </Card>
        <Card>
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-sm font-bold">XP ganho hoje</p>
              <p className="text-xs text-muted-foreground">Acumulado real do dia</p>
            </div>
            <Skeleton className="h-4 w-24" />
          </div>
          <p className="mt-3 text-xs text-muted-foreground">Carregando dados...</p>
        </Card>
      </section>

      <Card>
        <div className="mb-3 flex items-center justify-between gap-2">
          <p className="text-sm font-bold">Missões ativas</p>
          <span className="text-xs text-muted-foreground">Diária, Semanal e Mensal</span>
        </div>
        <div className="grid gap-3 lg:grid-cols-2 xl:grid-cols-3">
          {[0, 1, 2, 3, 4, 5].map((item) => (
            <div key={item} className="rounded-md border border-border bg-background p-3">
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="mt-2 h-3 w-11/12" />
              <Skeleton className="mt-3 h-2 w-full" />
              <div className="mt-3 flex items-center justify-between">
                <Skeleton className="h-4 w-16" />
                <Skeleton className="h-8 w-24" />
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  )
}

export function HabitsScreenSkeleton(): React.JSX.Element {
  return (
    <section className="flex flex-col gap-3">
      <div role="status" aria-live="polite" className="sr-only">
        Carregando hábitos...
      </div>
      <div className="grid gap-2 sm:grid-cols-2">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
      {[0, 1, 2, 3].map((item) => (
        <Card key={item} className="bg-background p-3">
          <div className="flex items-start justify-between gap-3">
            <div className="flex min-w-0 flex-1 items-center gap-2">
              <Skeleton className="h-9 w-9 rounded-full" />
              <div className="min-w-0 flex-1 flex flex-col gap-2">
                <Skeleton className="h-4 w-2/3" />
                <Skeleton className="h-3 w-1/3" />
              </div>
            </div>
            <div className="flex gap-2">
              <Skeleton className="h-8 w-8" />
              <Skeleton className="h-8 w-8" />
            </div>
          </div>
        </Card>
      ))}
    </section>
  )
}

export function GoalsScreenSkeleton(): React.JSX.Element {
  return (
    <section className="flex flex-col gap-3">
      {[0, 1, 2].map((item) => (
        <Card key={item} className="bg-background p-3">
          <div className="flex items-center justify-between gap-2">
            <Skeleton className="h-4 w-1/2" />
            <Skeleton className="h-4 w-16" />
          </div>
          <Skeleton className="mt-3 h-2 w-full" />
          <div className="mt-3 flex gap-2">
            <Skeleton className="h-7 w-20" />
            <Skeleton className="h-7 w-20" />
          </div>
        </Card>
      ))}
    </section>
  )
}

export function DiaryScreenSkeleton(): React.JSX.Element {
  return (
    <div className="flex flex-col gap-3">
      <div role="status" aria-live="polite" className="sr-only">
        Carregando diário...
      </div>
      <Card>
        <div className="grid gap-2 sm:grid-cols-2">
          <Skeleton className="h-9 w-full" />
          <Skeleton className="h-9 w-full" />
        </div>
      </Card>
      <Card>
        <div className="flex items-center justify-between gap-2">
          <Skeleton className="h-4 w-36" />
          <Skeleton className="h-8 w-40" />
        </div>
        <div className="mt-3 grid grid-cols-7 gap-2">
          {Array.from({ length: 28 }).map((_, index) => (
            <Skeleton key={index} className="h-8 w-full" />
          ))}
        </div>
      </Card>
      <Card>
        <Skeleton className="h-4 w-40" />
        <Skeleton className="mt-3 h-40 w-full" />
      </Card>
    </div>
  )
}

export function ProjectsTasksScreenSkeleton(): React.JSX.Element {
  return (
    <section className="flex flex-col gap-3">
      <Card>
        <div className="grid gap-2 sm:grid-cols-2">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      </Card>
      {[0, 1, 2].map((item) => (
        <Card key={item} className="bg-background p-3">
          <div className="flex items-center justify-between gap-2">
            <Skeleton className="h-4 w-1/2" />
            <Skeleton className="h-4 w-20" />
          </div>
          <Skeleton className="mt-3 h-2 w-full" />
          <div className="mt-3 flex flex-col gap-2">
            <Skeleton className="h-9 w-full" />
            <Skeleton className="h-9 w-full" />
          </div>
        </Card>
      ))}
    </section>
  )
}

export function ShopScreenSkeleton(): React.JSX.Element {
  return (
    <div className="flex flex-col gap-4">
      <div role="status" aria-live="polite" className="sr-only">
        Carregando loja...
      </div>
      <Skeleton className="h-28 w-full" />
      <div>
        <Skeleton className="mb-2 h-3 w-20" />
        <div className="flex gap-2 overflow-hidden">
          {[0, 1, 2, 3].map((item) => (
            <Skeleton key={item} className="h-28 w-[140px] shrink-0" />
          ))}
        </div>
      </div>
      <Skeleton className="h-10 w-full" />
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
        {[0, 1, 2, 3, 4, 5, 6, 7].map((item) => (
          <Card key={item} className="p-3">
            <Skeleton className="h-16 w-full" />
            <Skeleton className="mt-3 h-4 w-3/4" />
            <Skeleton className="mt-2 h-3 w-1/2" />
            <Skeleton className="mt-3 h-8 w-full" />
          </Card>
        ))}
      </div>
    </div>
  )
}

export function InventoryScreenSkeleton(): React.JSX.Element {
  return (
    <div className="flex flex-col gap-4">
      <Skeleton className="h-10 w-full" />
      <Skeleton className="h-10 w-full" />
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
        {[0, 1, 2, 3, 4, 5, 6, 7].map((item) => (
          <Card key={item} className="p-3">
            <Skeleton className="h-16 w-full" />
            <Skeleton className="mt-3 h-4 w-3/4" />
            <Skeleton className="mt-2 h-3 w-1/2" />
            <Skeleton className="mt-3 h-8 w-full" />
          </Card>
        ))}
      </div>
    </div>
  )
}

export function PassScreenSkeleton(): React.JSX.Element {
  return (
    <div className="flex flex-col gap-4 pb-4">
      <Card>
        <Skeleton className="h-3 w-36" />
        <Skeleton className="mt-2 h-8 w-56" />
        <Skeleton className="mt-2 h-4 w-64" />
        <div className="mt-3 flex gap-2">
          <Skeleton className="h-8 w-32" />
          <Skeleton className="h-8 w-20" />
        </div>
      </Card>
      <Card>
        <div className="flex items-center justify-between gap-2">
          <Skeleton className="h-3 w-32" />
          <Skeleton className="h-3 w-36" />
        </div>
        <Skeleton className="mt-2 h-2.5 w-full" />
      </Card>
      <div className="flex gap-2 overflow-hidden">
        {Array.from({ length: 6 }).map((_, index) => (
          <Card key={index} className="min-w-[180px] p-3">
            <Skeleton className="h-4 w-10" />
            <Skeleton className="mt-3 h-16 w-full" />
            <Skeleton className="mt-3 h-8 w-full" />
          </Card>
        ))}
      </div>
    </div>
  )
}

export function ProfileScreenSkeleton(): React.JSX.Element {
  return (
    <>
      <Card>
        <div className="flex items-center gap-3">
          <Skeleton className="h-16 w-16 rounded-full" />
          <div className="flex flex-col gap-2">
            <Skeleton className="h-5 w-40" />
            <Skeleton className="h-3 w-28" />
          </div>
        </div>
        <div className="mt-3 flex gap-2">
          <Skeleton className="h-7 w-7 rounded" />
          <Skeleton className="h-7 w-7 rounded" />
          <Skeleton className="h-7 w-7 rounded" />
        </div>
      </Card>
      <Card>
        <Skeleton className="h-4 w-16" />
        <div className="mt-4 flex flex-wrap gap-4">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="min-w-[200px] flex-1 flex flex-col gap-1.5">
              <Skeleton className="h-3 w-16" />
              <Skeleton className="h-9 w-full" />
            </div>
          ))}
        </div>
        <Skeleton className="mt-4 h-9 w-32" />
      </Card>
    </>
  )
}

export function LeaguesScreenSkeleton(): React.JSX.Element {
  return (
    <div className="flex flex-col gap-4 pb-4">
      <Card>
        <Skeleton className="h-3 w-28" />
        <Skeleton className="mt-2 h-8 w-52" />
        <Skeleton className="mt-2 h-4 w-64" />
      </Card>
      <Card>
        <div className="grid grid-cols-3 gap-2">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      </Card>
      <Card>
        <div className="flex flex-col gap-2">
          {[0, 1, 2, 3, 4, 5].map((item) => (
            <div key={item} className="flex items-center justify-between gap-3 rounded-md border border-border p-2">
              <Skeleton className="h-4 w-8" />
              <Skeleton className="h-4 w-full max-w-[220px]" />
              <Skeleton className="h-4 w-12" />
            </div>
          ))}
        </div>
      </Card>
    </div>
  )
}

export function ChallengesScreenSkeleton(): React.JSX.Element {
  return (
    <div className="flex flex-col gap-4 pb-4">
      <Card>
        <Skeleton className="h-3 w-24" />
        <Skeleton className="mt-2 h-8 w-56" />
        <Skeleton className="mt-2 h-4 w-72" />
        <div className="mt-3 flex gap-2">
          <Skeleton className="h-8 w-28" />
          <Skeleton className="h-8 w-28" />
        </div>
      </Card>
      <div className="grid gap-4 xl:grid-cols-12">
        <Card className="xl:col-span-3">
          <Skeleton className="h-4 w-28" />
          <Skeleton className="mt-3 h-24 w-full" />
        </Card>
        <div className="flex flex-col gap-3 xl:col-span-9">
          {[0, 1, 2].map((item) => (
            <Card key={item}>
              <Skeleton className="h-4 w-2/3" />
              <Skeleton className="mt-2 h-3 w-1/2" />
              <Skeleton className="mt-3 h-2 w-full" />
              <div className="mt-3 flex gap-2">
                <Skeleton className="h-8 w-24" />
                <Skeleton className="h-8 w-24" />
              </div>
            </Card>
          ))}
        </div>
      </div>
    </div>
  )
}

export function DuelsScreenSkeleton(): React.JSX.Element {
  return (
    <div className="flex flex-col gap-4 pb-4">
      <header className="rounded-lg border border-primary/30 bg-card p-4 sm:p-5">
        <div className="flex items-center gap-1.5">
          <Skeleton className="h-3.5 w-3.5 rounded-sm" />
          <Skeleton className="h-3 w-20" />
        </div>
        <Skeleton className="mt-2 h-8 w-52" />
        <Skeleton className="mt-2 h-4 w-72" />
        <Skeleton className="mt-3 h-8 w-36" />
      </header>
      <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_320px]">
        <div className="flex flex-col gap-3">
          {[0, 1, 2].map((item) => (
            <Card key={item}>
              <div className="flex items-center gap-2">
                <Skeleton className="h-10 w-10 rounded-full" />
                <Skeleton className="h-10 w-10 rounded-full" />
                <div className="w-full flex flex-col gap-2">
                  <Skeleton className="h-4 w-1/2" />
                  <Skeleton className="h-2 w-full" />
                </div>
              </div>
            </Card>
          ))}
        </div>
        <Card>
          <Skeleton className="h-4 w-32" />
          <div className="mt-3 flex flex-col gap-2">
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
          </div>
        </Card>
      </div>
    </div>
  )
}

export function BossScreenSkeleton(): React.JSX.Element {
  return (
    <div className="flex flex-col gap-4 pb-4">
      <Card className="p-0">
        <Skeleton className="h-56 w-full rounded-b-none rounded-t-lg" />
        <div className="p-4">
          <Skeleton className="h-3 w-24" />
          <Skeleton className="mt-2 h-8 w-56" />
          <Skeleton className="mt-2 h-4 w-72" />
          <Skeleton className="mt-3 h-9 w-40" />
        </div>
      </Card>
      <div className="grid gap-2 sm:grid-cols-3">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
      <Card>
        <Skeleton className="h-4 w-28" />
        <Skeleton className="mt-3 h-2.5 w-full" />
      </Card>
    </div>
  )
}

export function PetScreenSkeleton(): React.JSX.Element {
  return (
    <div className="flex flex-col gap-6 pb-4">
      <div className="flex items-start gap-4 rounded-lg border border-primary/30 bg-card p-4 sm:p-5">
        <div className="flex-1">
          <PageHeader
            title={<Skeleton className="h-9 w-48" />}
            description={<Skeleton className="h-4 w-64" />}
          />
        </div>
        <Skeleton className="size-32 md:size-48 rounded-2xl shrink-0" />
      </div>


      {/* Equipment Slots Skeleton */}
      <section className="flex flex-col gap-4">
        <Skeleton className="h-4 w-32 ml-1" />
        <div className="grid grid-cols-3 gap-4 max-w-2xl">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="aspect-square w-full rounded-lg" />
          ))}
        </div>
      </section>

      {/* Stats Skeleton (8 items) */}
      <section className="flex flex-col gap-4">
        <Skeleton className="h-4 w-40 ml-1" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
            <Skeleton key={i} className="h-20 w-full rounded-xl" />
          ))}
        </div>
      </section>

      {/* Other Pets Skeleton */}
      <section className="flex flex-col gap-4">
        <Skeleton className="h-4 w-32 ml-1" />
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="p-4 rounded-xl border border-border bg-card flex flex-col gap-3">
              <Skeleton className="size-10 rounded-lg" />
              <Skeleton className="h-3 w-3/4" />
              <Skeleton className="h-2 w-1/2" />
            </div>
          ))}
        </div>
      </section>
    </div>
  )
}

/* ─── Helper: Banner skeleton reused by settings/social/achievements ─── */

function BannerSkeleton({ statsCount = 0, showBackButton = false }: { statsCount?: number; showBackButton?: boolean }) {
  return (
    <header>
      <Skeleton className="h-3 w-28" />
      <Skeleton className="mt-2 h-6 w-52" />
      <Skeleton className="mt-2 h-4 w-72" />
      {statsCount > 0 ? (
        <div className="mt-3 flex flex-wrap gap-2">
          {Array.from({ length: statsCount }).map((_, i) => (
            <Skeleton key={i} className="h-6 w-24 rounded-full" />
          ))}
        </div>
      ) : null}
      {showBackButton ? <Skeleton className="mt-3 h-8 w-32" /> : null}
    </header>
  )
}

/* ─── Settings ─── */

export function SettingsMainScreenSkeleton(): React.JSX.Element {
  return (
    <div className="flex flex-col gap-4 pb-4">
      <BannerSkeleton statsCount={2} />
      <section className="grid gap-2 md:grid-cols-2">
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="flex items-center justify-between rounded-lg border border-border bg-card px-4 py-3">
            <div className="flex items-center gap-3">
              <Skeleton className="h-4 w-4" />
              <div className="flex flex-col gap-1.5">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-3 w-48" />
              </div>
            </div>
            <Skeleton className="h-4 w-4" />
          </div>
        ))}
      </section>
      <Card>
        <Skeleton className="h-4 w-40" />
        <div className="mt-3 flex flex-wrap gap-2">
          <Skeleton className="h-8 w-28" />
          <Skeleton className="h-8 w-40" />
          <Skeleton className="h-8 w-20" />
        </div>
      </Card>
      <Skeleton className="h-10 w-full" />
    </div>
  )
}

export function AccountScreenSkeleton(): React.JSX.Element {
  return (
    <div className="flex flex-col gap-4 pb-4">
      <BannerSkeleton showBackButton />
      <Card>
        <div className="flex flex-col gap-4">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="flex flex-col gap-1.5">
              <Skeleton className="h-3 w-32" />
              <Skeleton className="h-10 w-full" />
            </div>
          ))}
          <Skeleton className="h-9 w-36" />
        </div>
      </Card>
      <section className="rounded-lg border border-destructive/40 bg-destructive/10 p-4">
        <Skeleton className="h-4 w-28" />
        <Skeleton className="mt-2 h-3 w-64" />
        <Skeleton className="mt-3 h-9 w-36" />
      </section>
    </div>
  )
}

export function PrivacyScreenSkeleton(): React.JSX.Element {
  return (
    <div className="flex flex-col gap-4 pb-4">
      <BannerSkeleton showBackButton />
      <Card>
        <div className="flex flex-col gap-4">
          <div>
            <Skeleton className="h-3 w-36" />
            <div className="mt-2 flex gap-2">
              <Skeleton className="h-8 w-20" />
              <Skeleton className="h-8 w-20" />
              <Skeleton className="h-8 w-20" />
            </div>
            <Skeleton className="mt-2 h-3 w-56" />
          </div>
          {[0, 1].map((i) => (
            <div key={i} className="flex items-center justify-between rounded-md border p-3">
              <div className="flex flex-col gap-1">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-3 w-52" />
              </div>
              <Skeleton className="h-6 w-10" />
            </div>
          ))}
        </div>
      </Card>
      <Card>
        <Skeleton className="h-3 w-24" />
        <div className="mt-3 flex flex-col gap-4">
          {[0, 1].map((i) => (
            <div key={i}>
              <Skeleton className="h-3 w-44" />
              <div className="mt-2 flex gap-2">
                <Skeleton className="h-8 w-20" />
                <Skeleton className="h-8 w-20" />
                <Skeleton className="h-8 w-24" />
              </div>
            </div>
          ))}
        </div>
      </Card>
      <div className="flex justify-end">
        <Skeleton className="h-9 w-36" />
      </div>
    </div>
  )
}

export function NotificationsScreenSkeleton(): React.JSX.Element {
  return (
    <div className="flex flex-col gap-4 pb-4">
      <BannerSkeleton showBackButton />
      <div className="grid gap-4 md:grid-cols-2">
        {Array.from({ length: 8 }).map((_, i) => (
          <div key={i} className="flex items-center justify-between rounded-lg border border-border bg-card/50 p-4">
            <div className="flex flex-col gap-1">
              <Skeleton className="h-4 w-28" />
              <Skeleton className="h-3 w-44" />
            </div>
            <Skeleton className="h-6 w-10" />
          </div>
        ))}
      </div>
      <section className="rounded-lg border border-primary/20 bg-primary/5 p-4 flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <div className="flex flex-col gap-1">
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-3 w-48" />
          </div>
          <Skeleton className="h-6 w-10" />
        </div>
        <div className="flex gap-2">
          <Skeleton className="h-8 w-20" />
          <Skeleton className="h-8 w-20" />
          <Skeleton className="h-8 w-20" />
        </div>
      </section>
      <div className="flex justify-end">
        <Skeleton className="h-9 w-36" />
      </div>
    </div>
  )
}

export function ModulesScreenSkeleton(): React.JSX.Element {
  return (
    <div className="flex flex-col gap-4 pb-4">
      <BannerSkeleton showBackButton />
      <section className="flex flex-col gap-2">
        {[0, 1, 2, 3].map((i) => (
          <article key={i} className="rounded-lg border border-border bg-card p-4">
            <div className="flex items-center justify-between gap-3">
              <div className="flex flex-col gap-1.5">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-3 w-52" />
              </div>
              <Skeleton className="h-7 w-16 rounded-md" />
            </div>
          </article>
        ))}
      </section>
    </div>
  )
}

export function AccessibilityScreenSkeleton(): React.JSX.Element {
  return (
    <div className="flex flex-col gap-4 pb-4">
      <BannerSkeleton showBackButton />
      <Card>
        <div className="flex flex-col gap-4">
          {[0, 1].map((i) => (
            <div key={i} className="flex items-center justify-between rounded-lg border border-border/60 bg-background/50 p-3">
              <div className="flex flex-col gap-1">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-3 w-52" />
              </div>
              <Skeleton className="h-6 w-10" />
            </div>
          ))}
          <div className="flex flex-col gap-1.5">
            <Skeleton className="h-3 w-28" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-3 w-56" />
          </div>
        </div>
      </Card>
      <div className="flex justify-end">
        <Skeleton className="h-9 w-36" />
      </div>
    </div>
  )
}

export function SubscriptionScreenSkeleton(): React.JSX.Element {
  return (
    <div className="flex flex-col gap-4 pb-4">
      <BannerSkeleton showBackButton />
      <Card>
        <Skeleton className="h-4 w-28" />
        <div className="mt-3 rounded-md border border-border bg-background p-3">
          <Skeleton className="h-4 w-36" />
          <Skeleton className="mt-2 h-3 w-56" />
          <Skeleton className="mt-1 h-3 w-48" />
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          <Skeleton className="h-9 w-44" />
          <Skeleton className="h-9 w-36" />
        </div>
      </Card>
      <Card>
        <Skeleton className="h-4 w-44" />
        <div className="mt-3 flex flex-col gap-2">
          {[0, 1, 2].map((i) => (
            <div key={i} className="rounded-md border border-border bg-background p-3">
              <Skeleton className="h-3 w-32" />
              <Skeleton className="mt-1 h-3 w-24" />
              <Skeleton className="mt-1 h-3 w-16" />
            </div>
          ))}
        </div>
      </Card>
    </div>
  )
}

/* ─── Billing ─── */

export function BillingSuccessScreenSkeleton(): React.JSX.Element {
  return (
    <div className="flex flex-col gap-4 pb-4">
      <Card>
        <Skeleton className="h-12 w-12 rounded-full" />
        <Skeleton className="mt-3 h-6 w-52" />
        <Skeleton className="mt-2 h-4 w-72" />
        <div className="mt-3 rounded-md border border-border bg-background p-3">
          <Skeleton className="h-4 w-48" />
          <Skeleton className="mt-1 h-3 w-36" />
        </div>
      </Card>
      <div className="flex gap-2">
        <Skeleton className="h-10 w-40" />
        <Skeleton className="h-10 w-32" />
      </div>
    </div>
  )
}

export function BillingCancelScreenSkeleton(): React.JSX.Element {
  return (
    <div className="flex flex-col gap-4 pb-4">
      <Card>
        <Skeleton className="h-12 w-12 rounded-full" />
        <Skeleton className="mt-3 h-6 w-48" />
        <Skeleton className="mt-2 h-4 w-64" />
      </Card>
      <Card className="border-destructive/40">
        <Skeleton className="h-4 w-56" />
      </Card>
      <div className="flex gap-2">
        <Skeleton className="h-10 w-36" />
        <Skeleton className="h-10 w-28" />
      </div>
    </div>
  )
}

/* ─── Social ─── */

export function FriendsScreenSkeleton(): React.JSX.Element {
  return (
    <div className="flex flex-col gap-4 pb-4">
      <PageHeader
        title="Amigos"
        description="Carregando lista de aventureiros..."
        actions={<Skeleton className="h-9 w-32" />}
      />
      <div className="grid grid-cols-3 gap-2">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
      <section className="grid gap-2 md:grid-cols-2">
        {[0, 1, 2, 3].map((i) => (
          <Card key={i}>
            <div className="flex items-center gap-3">
              <Skeleton className="h-10 w-10 rounded-full" />
              <div className="min-w-0 flex-1 flex flex-col gap-1.5">
                <Skeleton className="h-4 w-28" />
                <Skeleton className="h-3 w-20" />
              </div>
            </div>
            <div className="mt-3 flex gap-2">
              <Skeleton className="h-8 w-20" />
              <Skeleton className="h-8 w-20" />
            </div>
          </Card>
        ))}
      </section>
    </div>
  )
}

export function InvitesScreenSkeleton(): React.JSX.Element {
  return (
    <div className="flex flex-col gap-4 pb-4">
      <BannerSkeleton statsCount={1} />
      <Card>
        <Skeleton className="h-4 w-36" />
        <Skeleton className="mt-3 h-10 w-full" />
        <div className="mt-3 flex gap-2">
          <Skeleton className="h-9 w-28" />
          <Skeleton className="h-9 w-32" />
        </div>
      </Card>
      <Card>
        <Skeleton className="h-4 w-40" />
        <div className="mt-3 flex flex-col gap-2">
          {[0, 1, 2].map((i) => (
            <div key={i} className="rounded-md border border-border bg-background p-3">
              <Skeleton className="h-3 w-44" />
              <Skeleton className="mt-1 h-3 w-28" />
            </div>
          ))}
        </div>
      </Card>
    </div>
  )
}

export function PlayerCardModalSkeleton(): React.JSX.Element {
  return (
    <div className="flex flex-col gap-4 p-2">
      <div className="flex items-center gap-3">
        <Skeleton className="h-16 w-16 rounded-full" />
        <div className="flex flex-col gap-2">
          <Skeleton className="h-5 w-36" />
          <Skeleton className="h-3 w-24" />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="rounded-md border border-border bg-background p-2">
            <Skeleton className="h-3 w-16" />
            <Skeleton className="mt-1 h-4 w-12" />
          </div>
        ))}
      </div>
      <div className="flex gap-2">
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} className="h-8 w-8 rounded" />
        ))}
      </div>
    </div>
  )
}

/* ─── Manage Forms ─── */

export function HabitFormScreenSkeleton(): React.JSX.Element {
  return (
    <div className="flex flex-col gap-4 pb-4">
      <div role="status" aria-live="polite" className="sr-only">
        Carregando formulário...
      </div>
      <BannerSkeleton showBackButton />
      <Card>
        <Skeleton className="h-4 w-32" />
        <Skeleton className="mt-3 h-32 w-full" />
      </Card>
      <Card>
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <Skeleton className="h-3 w-20" />
            <Skeleton className="h-10 w-full" />
          </div>
          <div className="flex flex-col gap-1.5">
            <Skeleton className="h-3 w-28" />
            <Skeleton className="h-10 w-full" />
          </div>
          <div className="rounded-md border border-border p-3 flex flex-col gap-3">
            <Skeleton className="h-3 w-32" />
            <div className="flex gap-2">
              <Skeleton className="h-8 w-24" />
              <Skeleton className="h-8 w-28" />
              <Skeleton className="h-8 w-24" />
            </div>
          </div>
          <div className="rounded-md border border-border p-3 flex flex-col gap-3">
            <Skeleton className="h-3 w-28" />
            <div className="flex gap-2">
              <Skeleton className="h-8 w-20" />
              <Skeleton className="h-8 w-28" />
              <Skeleton className="h-8 w-24" />
            </div>
          </div>
          <div className="flex items-center justify-between rounded-md border p-3">
            <div className="flex flex-col gap-1">
              <Skeleton className="h-4 w-28" />
              <Skeleton className="h-3 w-44" />
            </div>
            <Skeleton className="h-6 w-10" />
          </div>
          <Skeleton className="h-10 w-full" />
        </div>
      </Card>
    </div>
  )
}

export function GoalFormScreenSkeleton(): React.JSX.Element {
  return (
    <div className="flex flex-col gap-4 pb-4">
      <BannerSkeleton showBackButton />
      <Card>
        <div className="flex flex-col gap-4">
          <div className="flex gap-3 items-end">
            <Skeleton className="h-[72px] w-[40px]" />
            <div className="flex flex-col gap-1.5 flex-1">
              <Skeleton className="h-3 w-24" />
              <Skeleton className="h-9 w-full" />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="flex flex-col gap-1.5">
              <Skeleton className="h-3 w-20" />
              <Skeleton className="h-10 w-full" />
            </div>
            <div className="flex flex-col gap-1.5">
              <Skeleton className="h-3 w-16" />
              <Skeleton className="h-10 w-full" />
            </div>
            <div className="flex flex-col gap-1.5">
              <Skeleton className="h-3 w-24" />
              <Skeleton className="h-10 w-full" />
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <Skeleton className="h-3 w-32" />
            {[0, 1, 2].map((i) => (
              <div key={i} className="flex items-center gap-2">
                <Skeleton className="h-4 w-4" />
                <Skeleton className="h-4 w-36" />
              </div>
            ))}
          </div>
          <Skeleton className="h-10 w-full" />
        </div>
      </Card>
    </div>
  )
}

/* ─── Contracts ─── */

export function YearResetScreenSkeleton(): React.JSX.Element {
  return (
    <div className="flex flex-col gap-4 pb-4">
      <BannerSkeleton />
      <Card>
        <Skeleton className="h-4 w-36" />
        <div className="mt-3 grid grid-cols-2 gap-2">
          {[0, 1].map((i) => (
            <div key={i} className="rounded-md border border-border bg-background p-3 text-center">
              <Skeleton className="mx-auto h-3 w-16" />
              <Skeleton className="mx-auto mt-2 h-6 w-12" />
            </div>
          ))}
        </div>
        <Skeleton className="mt-4 h-10 w-full" />
      </Card>
    </div>
  )
}

export function SeasonResetScreenSkeleton(): React.JSX.Element {
  return (
    <div className="flex flex-col gap-4 pb-4">
      <BannerSkeleton />
      <Card>
        <Skeleton className="h-4 w-36" />
        <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-3">
          {[0, 1, 2, 3, 4].map((i) => (
            <div key={i} className="rounded-md border border-border bg-background p-3 text-center">
              <Skeleton className="mx-auto h-3 w-16" />
              <Skeleton className="mx-auto mt-2 h-6 w-12" />
            </div>
          ))}
        </div>
        <Skeleton className="mt-4 h-10 w-full" />
      </Card>
    </div>
  )
}

/* ─── Dashboard ─── */

export function AchievementsScreenSkeleton(): React.JSX.Element {
  return (
    <div className="flex flex-col gap-4 pb-4">
      <BannerSkeleton statsCount={2} />
      <div className="flex flex-wrap gap-2">
        {[0, 1, 2, 3, 4].map((i) => (
          <Skeleton key={i} className="h-8 w-20 rounded-full" />
        ))}
      </div>
      <Skeleton className="h-10 w-40" />
      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <Card key={i}>
            <div className="flex items-start gap-3">
              <Skeleton className="h-10 w-10 shrink-0 rounded" />
              <div className="min-w-0 flex-1 flex flex-col gap-1.5">
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-3 w-16 rounded-full" />
                <Skeleton className="h-3 w-full" />
              </div>
            </div>
            <Skeleton className="mt-3 h-2 w-full" />
          </Card>
        ))}
      </div>
    </div>
  )
}

/* ─── Support ─── */

export function SupportScreenSkeleton(): React.JSX.Element {
  return (
    <div className="flex flex-col gap-4 pb-4">
      <BannerSkeleton />
      <Card>
        <Skeleton className="h-4 w-28" />
        <div className="mt-3 flex flex-col gap-3">
          <div className="flex flex-col gap-1.5">
            <Skeleton className="h-3 w-20" />
            <Skeleton className="h-10 w-full" />
          </div>
          <div className="flex flex-col gap-1.5">
            <Skeleton className="h-3 w-24" />
            <Skeleton className="h-10 w-full" />
          </div>
          <div className="flex flex-col gap-1.5">
            <Skeleton className="h-3 w-24" />
            <Skeleton className="h-24 w-full" />
          </div>
          <Skeleton className="h-10 w-full" />
        </div>
      </Card>
      <Card>
        <Skeleton className="h-4 w-32" />
        <div className="mt-3 flex flex-col gap-2">
          {[0, 1, 2].map((i) => (
            <div key={i} className="rounded-md border border-border bg-background p-3">
              <Skeleton className="h-4 w-2/3" />
              <Skeleton className="mt-1 h-3 w-1/3" />
              <Skeleton className="mt-2 h-3 w-full" />
            </div>
          ))}
        </div>
      </Card>
      <Card>
        <Skeleton className="h-4 w-28" />
        <div className="mt-3 flex flex-wrap gap-2">
          <Skeleton className="h-8 w-28" />
          <Skeleton className="h-8 w-24" />
          <Skeleton className="h-8 w-20" />
        </div>
      </Card>
    </div>
  )
}
